/**
 * Fare Calendar API
 *
 * Network-only functions for fare calendar operations.
 * NO Redux imports allowed in this file.
 *
 * Uses common flights API utilities which automatically add:
 * - Headers: X-Gommt-Mmt-Auth, X-Gommt-Uuid, X-Device-Id
 * - Query params: sessionId, userId
 */
import type { FareCalendarTemplate } from '@contracts';
import { FLIGHTS_ENDPOINTS } from '@/api/endpoints';
import { buildHeaders } from '@/api/utils';
import { logger } from '@/shared/utils/logger';

// ============================================
// Error Classes
// ============================================

export class FetchFareCalendarError extends Error {
  constructor(
    message: string,
    public readonly statusCode?: number,
    public readonly cause?: unknown,
  ) {
    super(message);
    this.name = 'FetchFareCalendarError';
  }
}

// ============================================
// Request/Response Types
// ============================================

/** Cabin class types */
export type CabinClass = 'E' | 'PE' | 'B' | 'F'; // Economy, Premium Economy, Business, First

/** Trip type */
export type TripType = 'OW' | 'RT' | 'MC'; // One-way, Round-trip, Multi-city

/** Single itinerary leg */
export interface FareCalendarItineraryLeg {
  /** Origin airport code (e.g., "DEL") */
  from: string;
  /** Destination airport code (e.g., "BLR") */
  to: string;
  /** Date in YYYYMMDD format (e.g., "20260225") */
  date: string;
}

/** Core request parameters for fare calendar API */
export interface FareCalendarCoreRequest {
  /** Cabin class: E (Economy), PE (Premium Economy), B (Business), F (First) */
  cabinClass: CabinClass;
  /** Array of itinerary legs - 1 for OW, 2 for RT, 2+ for MC */
  itinerary: FareCalendarItineraryLeg[];
  /** Number of adult passengers */
  numAdults: number;
  /** Number of child passengers */
  numChildren: number;
  /** Number of infant passengers */
  numInfant: number;
  /** Trip type: OW (One-way), RT (Round-trip), MC (Multi-city) */
  tripType: TripType;
}

/** Full request parameters for fare calendar API */
export interface FetchFareCalendarRequestParams {
  /** User query (e.g., "Show fare calendar") */
  query?: string;
  /** User ID */
  userId: string;
  /** Session ID */
  sessionId: string;
  /** Core request with itinerary details */
  coreRequest: FareCalendarCoreRequest;
}

// /** API response wrapper */
// interface FareCalendarApiResponse {
//   type?: string;
//   data?: FareCalendarTemplateData;
//   fareCalendar?: FareCalendarTemplateData['fareCalendar'];
// }

// ============================================
// API Functions
// ============================================

/**
 * Fetch fare calendar data from API
 *
 * Uses common flights API utilities with automatic:
 * - Headers: X-Gommt-Mmt-Auth, X-Gommt-Uuid, X-Device-Id
 *
 * @param params - Request parameters including coreRequest with itinerary
 * @returns Promise<FareCalendarTemplateData>
 */
export async function fetchFareCalendar(
  params: FetchFareCalendarRequestParams,
): Promise<FareCalendarTemplate> {
  const requestBody = {
    query: params.query ?? 'Show fare calendar',
    userId: params.userId,
    sessionId: params.sessionId,
    coreRequest: params.coreRequest,
  };

  const headers = buildHeaders();

  logger.info('[FareCalendarApi] Fetching fare calendar:', {
    endpoint: FLIGHTS_ENDPOINTS.FARE_CALENDAR,
    tripType: params.coreRequest.tripType,
    itinerary: params.coreRequest.itinerary,
  });

  try {
    const response = await fetch(FLIGHTS_ENDPOINTS.FARE_CALENDAR, {
      method: 'POST',
      headers,
      body: JSON.stringify(requestBody),
    });

    if (!response.ok) {
      throw new FetchFareCalendarError(
        `API request failed with status ${response.status}`,
        response.status,
      );
    }

    const apiResponse: FareCalendarTemplate = await response.json();
    if (apiResponse.activeTemplate !== 'fare-calendar') {
      throw new FetchFareCalendarError('Invalid API response structure: missing fareCalendar data');
    }

    return apiResponse;
  } catch (error) {
    logger.error('[FareCalendarApi] Failed to fetch fare calendar:', error);

    if (error instanceof FetchFareCalendarError) {
      throw error;
    }

    throw new FetchFareCalendarError('Failed to fetch fare calendar', undefined, error);
  }
}
